<template>
  <div class="relative flex h-full flex-col gap-4 overflow-visible">
    <div :class="dataTableHeadFilterClass">
      <div v-if="filterConfigs?.length" ref="showFiltersDiv" class="relative z-30 w-auto md:w-48">
        <InfuseButton
          class="w-full px-2 md:min-w-[160px]"
          color="secondary"
          :size="filterButtonSize"
          :aria-label="t('shared.component.infuse.table.action.addFilter')"
          @click="showFilters = !showFilters"
        >
          <div class="flex items-center justify-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'arrow-down-wide-short']" />
            <span class="hidden md:inline">{{ t('shared.component.infuse.table.action.addFilter') }}</span>
          </div>
        </InfuseButton>

        <InfuseFilterDropdown
          v-if="showFilters"
          :model-value="filterConfigs || []"
          :ignore-close-on-click="showFiltersDiv ? [showFiltersDiv] : []"
          @add:filter="(f: Filter) => emit('add:filter', f)"
          @close="showFilters = false"
        />
      </div>

      <slot name="actionsStart" />

      <InfuseInput
        v-if="search"
        :model-value="search.query"
        :placeholder="search.placeholder"
        name="search"
        :size="searchSize"
        autofocus
        class="grow-1"
        @update:model-value="debounceSearch"
      >
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" size="1x" class="text-neutral-300" />

        <template #clear="{ value }">
          <font-awesome-icon
            v-if="value"
            :icon="['fas', 'xmark']"
            size="1x"
            class="cursor-pointer text-neutral-300 transition-colors duration-150 ease-out-quart hover:text-primary-500 active:text-primary-700"
            @click="emit('search', '')"
          />
        </template>
      </InfuseInput>

      <slot name="actions" />

      <InfuseIconButton v-if="refreshable" :icon="['fas', 'refresh']" class="pr-6" size="sm" @click="emit('refresh')" />
    </div>

    <!-- Second toolbar row for actions that don't fit beside the search; gets the selection so consumers need not track it. -->
    <div v-if="$slots.secondaryActions" class="flex flex-wrap items-center gap-2">
      <slot name="secondaryActions" :selected-rows="allSelected" />
    </div>

    <div v-if="filters.length" class="flex flex-wrap gap-2">
      <div
        v-for="filter in filters"
        :key="`${filter.config.name}.${filter.selectedOption.name}${filter.negated ? '!' : ''}=${filter.searchQuery ?? ''}`"
        class="flex gap-2"
      >
        <InfusePill class="select-none" closable :label="filter.config.label()" @click="emit('remove:filter', filter)">
          {{
            `${determineOperator(filter)} ${
              filter.displayValue
                ? filter.displayValue
                : filter.searchQuery
                ? filter.searchQuery.toLowerCase()
                : filter.selectedOption.label().toLowerCase()
            }`
          }}
        </InfusePill>
      </div>
    </div>

    <div v-if="loading && rows.length === 0" class="mt-16 flex w-full justify-center">
      <InfuseSpinner size="large" />
    </div>

    <div v-else ref="dataTable" class="relative h-full overflow-y-visible" :class="dataTableClass">
      <table class="relative w-full text-left max-md:block">
        <thead
          class="sticky z-10 hidden w-full text-xs font-medium capitalize md:table-header-group"
          :class="dataTableHeadClass"
        >
          <tr>
            <th v-if="selectable" scope="col" class="py-3 pr-3 pl-6">
              <div class="flex items-center">
                <InfuseCheckBox
                  id="checkbox-all"
                  :model-value="allRowsAppearSelected"
                  @update:model-value="$event ? selectAll() : deselectAll()"
                />
              </div>
            </th>

            <th
              v-for="column in columnsWithSortState"
              :key="column.name"
              scope="col"
              class="cursor-pointer overflow-hidden text-ellipsis py-3 px-6 font-medium"
              :class="[
                column.align === UiTableColumnAlignment.CENTER ? 'text-center' : '',
                column.align === UiTableColumnAlignment.LEFT ? 'text-left' : '',
                column.align === UiTableColumnAlignment.RIGHT ? 'text-right' : '',
                column.maxWidth ? `max-w-[${column.maxWidth}]` : '',
                `infuse-table-column-${column.name}`
              ]"
              @click="() => column.sortable && sort(column.state)"
            >
              <span class="inline line-clamp-1">
                {{ column.label() }}
                <font-awesome-icon v-if="column.sortable" class="ml-1" :icon="getSortIcon(column.state.order)" />
                <InfuseTooltip
                  v-if="column.tooltip"
                  :content="column.tooltip()"
                  placement="top"
                  offset-distance="6"
                  class="normal-case"
                >
                  <font-awesome-icon
                    :icon="['fal', 'circle-info']"
                    class="ml-1 cursor-help text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-600"
                    @click.stop
                  />
                </InfuseTooltip>
              </span>
            </th>

            <th v-if="actions.length > 0" scope="col" class="py-3 px-6">
              <span class="sr-only">{{ t('shared.component.infuse.table.header.actions') }}</span>
            </th>

            <th v-if="downloadable || editable || removable || $slots.rowActions" scope="col" class="py-3 px-6">
              <span class="sr-only">{{ t('shared.component.infuse.table.header.editRemove') }}</span>
            </th>
          </tr>
        </thead>

        <slot v-if="!loading && rows.length === 0" name="noResults">
          <tr>
            <td :colspan="normalizedColumns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0) + (downloadable || editable || removable || $slots.rowActions ? 1 : 0)">
              <div class="my-20 flex w-full max-w-[550px] flex-col gap-8 justify-self-center text-center">
                <div class="flex flex-col items-center gap-4 overflow-visible">
                  <div class="h-40 w-40 bg-empty-state bg-contain bg-center bg-no-repeat" />
                  <p class="-mt-14 text-center text-xl font-bold">{{ noDataTitle || t('common.noData') }}</p>
                  <p class="-mt-4 whitespace-pre-line text-center text-sm">
                    {{ noDataDescription || t('common.noDataDescription') }}
                  </p>

                  <slot name="noResultsActions" />
                </div>
              </div>
            </td>
          </tr>
        </slot>

        <tbody class="text-xs font-medium max-md:block" :class="dataTableBodyClass">
          <tr
            v-for="(row, i) in rows"
            :key="`${getRowKey(row)}-${i}`"
            class="observable group max-md:mb-3 max-md:flex max-md:flex-wrap max-md:items-stretch max-md:rounded-lg max-md:border max-md:border-neutral-100"
            :class="[
              tableSlots.mobileCard ? 'max-md:overflow-hidden' : 'max-md:p-3',
              active(row, i) ? 'text-primary-500 ' : '',
              matchingSelected || selectedRows.includes(row) ? 'bg-neutral-50' : '',
              selectedRows.length === 0 && highlightRow === i ? 'bg-neutral-50' : '',
              isRowDisabled(row) ? 'opacity-50' : '',
              row.readonly ? 'cursor-text text-neutral-500' : (rowIsClickable(row) ? 'cursor-pointer' : 'cursor-default'),
              dataTableBodyRowClass
            ]"
            @click.stop="clickRow(row)"
          >
            <td
              v-if="selectable"
              :class="[
                verticalSize === 'compact' ? 'py-1 px-6' : '',
                verticalSize === 'medium' ? 'py-2 px-6' : '',
                verticalSize === 'expanded' ? 'py-3 px-6' : '',
                { 'max-md:hidden': tableSlots.mobileCard }
              ]"
              class="w-4 rounded-l-md max-md:flex max-md:w-auto max-md:shrink-0 max-md:items-center max-md:py-0 max-md:pl-3 max-md:pr-0"
              @click.stop=""
            >
              <div class="flex items-center">
                <slot name="selectRow" :data="row">
                  <!-- `matchingSelected`: the selection is the filter, so `selectedRows` is
                       empty while everything matching is selected — loaded rows show ticked
                       unless excluded (rows loaded later tick automatically too). -->
                  <InfuseCheckBox
                    v-if="!isRowDisabled(row)"
                    :model-value="isRowChecked(row)"
                    @update:model-value="selectRow($event, row)"
                  />
                </slot>
              </div>
            </td>

            <td
              v-for="(column, j) in normalizedColumns"
              :key="`column-field-attr-${j}`"
              class="hidden md:table-cell overflow-hidden break-all"
              :class="[
                column.bold
                  ? active(row, i)
                    ? 'font-semibold text-primary-500'
                    : 'font-semibold text-neutral-700'
                  : '',
                verticalSize === 'compact' ? 'whitespace-nowrap py-1 px-6' : '',
                verticalSize === 'medium' ? 'whitespace-nowrap py-2 px-6' : '',
                verticalSize === 'expanded' ? 'whitespace-nowrap py-3 px-6' : '',
                column.align === UiTableColumnAlignment.CENTER ? 'text-center' : '',
                column.align === UiTableColumnAlignment.LEFT ? 'text-left' : '',
                column.align === UiTableColumnAlignment.RIGHT ? 'text-right' : '',
                `infuse-table-column-${column.name}`
              ]"
            >
              <InfuseCopy v-if="column.copyable && get(row, column.field)" v-slot="scope" @click.stop="">
                <!-- @click.stop: stop bubbling up -->
                <slot :name="column.name" :data="get(row, column.field)" :row="row" :index="j" :active="active(row, i)">
                  <span @click="scope.copy(get(row, column.field).toString())">{{ get(row, column.field) }}</span>
                </slot>
                <font-awesome-icon
                  class="ml-1 cursor-pointer text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-600"
                  :icon="['far', 'copy']"
                  @click="scope.copy(get(row, column.field).toString())"
                />
              </InfuseCopy>
              <slot
                v-else
                :name="column.name"
                :data="get(row, column.field)"
                :row="row"
                :index="j"
                :row-index="i"
                :active="active(row, i)"
              >
                {{ get(row, column.field) }}
              </slot>
            </td>

            <!-- Mobile table: custom summary card when provided ... -->
            <td v-if="tableSlots.mobileCard" class="flex min-w-0 flex-1 items-center md:hidden">
              <!-- A custom card places the checkbox itself (e.g. after a status strip); the cell above hides on mobile. -->
              <slot
                name="mobileCard"
                :row="row"
                :active="active(row, i)"
                :selectable="selectable && !isRowDisabled(row)"
                :checked="isRowChecked(row)"
                :toggle="(isSelected: boolean) => selectRow(isSelected, row)"
              />
              <font-awesome-icon :icon="['fal', 'chevron-right']" class="mx-3 ml-auto shrink-0 text-neutral-300" />
            </td>

            <!-- ... otherwise an aligned label/value grid; rows without a value are skipped -->
            <td v-else class="grid min-w-0 flex-1 grid-cols-[minmax(0,35%)_1fr] items-start gap-x-3 gap-y-2.5 md:hidden">
              <template v-for="(column, j) in normalizedColumns" :key="`column-field-attr-${j}`">
                <template v-if="hasMobileValue(row, column)">
                  <div class="font-medium text-neutral-500">{{ column.label() }}</div>

                  <div class="min-w-0 break-words">
                    <InfuseCopy v-if="column.copyable && get(row, column.field)" v-slot="scope" @click.stop="">
                      <!-- @click.stop: stop bubbling up -->
                      <slot
                        :name="column.name"
                        :data="get(row, column.field)"
                        :row="row"
                        :index="j"
                        :active="active(row, i)"
                      >
                        <span @click="scope.copy(get(row, column.field).toString())">{{ get(row, column.field) }}</span>
                      </slot>
                      <font-awesome-icon
                        class="ml-1 cursor-pointer text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-600"
                        :icon="['far', 'copy']"
                        @click="scope.copy(get(row, column.field).toString())"
                      />
                    </InfuseCopy>

                    <div v-else :class="{ 'font-semibold text-neutral-700': column.bold }">
                      <slot
                        :name="column.name"
                        :data="get(row, column.field)"
                        :row="row"
                        :index="j"
                        :row-index="i"
                        :active="active(row, i)"
                      >
                        {{ get(row, column.field) }}
                      </slot>
                    </div>
                  </div>
                </template>
              </template>
            </td>

            <td
              v-if="actions.length > 0"
              class="whitespace-nowrap py-3 px-6"
              :class="tableSlots.mobileCard ? 'max-md:block max-md:basis-full max-md:px-3 max-md:pt-0 max-md:pb-3' : 'max-md:block max-md:basis-full max-md:p-0 max-md:pt-2'"
            >
              <div class="flex justify-end gap-2">
                <span
                  v-for="(action, idx) in actions"
                  :key="`action-${idx}`"
                  :class="action.onlyShowOnHover && 'invisible group-hover:visible max-md:visible'"
                >
                  <InfuseTooltip :content="action.label()" :offset-distance="'6'" placement="left">
                    <button
                      v-if="action.enabled(row)"
                      type="button"
                      :aria-label="action.label()"
                      :data-testid="`row-action-${action.event}`"
                      :class="
                        action.class ??
                        'cursor-pointer text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-primary-500'
                      "
                      @click.stop="$emit('action', action.event, row)"
                    >
                      <font-awesome-icon
                        :icon="action.icon.length > 1 ? [action.icon[0], action.icon[1]] : ['fas', action.icon]"
                        size="lg"
                      />
                    </button>
                  </InfuseTooltip>
                </span>
              </div>
            </td>

            <td
              v-if="!row.readonly && (editable || removable || downloadable || $slots.rowActions)"
              class="py-4 px-6 text-center text-sm"
              :class="tableSlots.mobileCard ? 'max-md:block max-md:basis-full max-md:px-3 max-md:pt-0 max-md:pb-3' : 'max-md:block max-md:basis-full max-md:p-0 max-md:pt-2'"
            >
              <div
                v-if="$slots.rowActions"
                class="flex items-center text-neutral-500 hover:text-neutral-500"
                :class="dataTableBodyRowActionsClass"
              >
                <slot name="rowActions" :row="row" />
              </div>
              <div
                class="invisible flex items-center text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-neutral-500 group-hover:visible max-md:visible"
                :class="dataTableBodyRowActionsClass"
              >
                <!-- Download -->
                <button
                  v-if="downloadable"
                  v-permission="{
                    ids: downloadPermissionIds,
                    hide: false
                  }"
                  type="button"
                  :aria-label="t('action.download')"
                  data-testid="row-download-action"
                  class="p-1 transition-colors duration-150 ease-out-quart hover:text-primary-500"
                  @click.stop="$emit('download', row)"
                >
                  <slot name="downloadRow">
                    <FontAwesomeIcon :icon="faDownload" size="1x" />
                  </slot>
                </button>

                <!-- Edit -->
                <button
                  v-if="editable"
                  v-permission="{
                    ids: editPermissionIds,
                    hide: false
                  }"
                  type="button"
                  :aria-label="t('action.edit')"
                  data-testid="row-edit-action"
                  class="p-1 transition-colors duration-150 ease-out-quart hover:text-primary-500"
                  @click.stop="$emit('edit', row)"
                >
                  <slot name="editRow">
                    <font-awesome-icon :icon="['far', 'pen-to-square']" size="1x" />
                  </slot>
                </button>

                <!-- Delete -->
                <button
                  v-if="rowIsRemovable(row)"
                  v-permission="{
                    ids: removePermissionIds,
                    hide: false
                  }"
                  type="button"
                  :aria-label="t('action.remove')"
                  data-testid="row-delete-action"
                  class="p-1 text-neutral-500 transition-colors duration-150 ease-out-quart hover:text-error-500"
                  @click.stop="$emit('remove', row)"
                >
                  <slot name="deleteRow">
                    <font-awesome-icon :icon="['far', 'trash-can']" size="1x" />
                  </slot>
                </button>
                <InfuseTooltip
                  v-else-if="removable"
                  v-permission="{
                    ids: removePermissionIds,
                    hide: false
                  }"
                  :content="rowRemovableTooltip ? rowRemovableTooltip(row) : ''"
                  offset-distance="4"
                >
                  <button
                    type="button"
                    disabled
                    :aria-label="t('action.remove')"
                    data-testid="row-delete-action"
                    class="cursor-not-allowed p-1 text-neutral-300"
                    @click.stop=""
                  >
                    <slot name="deleteRow">
                      <font-awesome-icon :icon="['far', 'trash-can']" size="1x" />
                    </slot>
                  </button>
                </InfuseTooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="flex w-full justify-center p-3">
        <InfuseSpinner size="medium" />
      </div>

      <div v-if="hasMoreItems" class="flex w-full justify-center p-3">
        <InfuseButton :color="loadMoreColor ?? 'primary'" :disabled="loading" @click="$emit('load')">
          {{ t('shared.component.infuse.table.loadMore') }}
        </InfuseButton>
      </div>
    </div>

    <template v-if="hasSelection && showSelectionChips">
      <div class="sticky bottom-0 z-10 w-full m-auto shrink-0">
        <div class="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <button
            type="button"
            class="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            @click="chipsExpanded = !chipsExpanded"
          >
            <component :is="selectionChipIconComponent" v-if="selectionChipIconComponent" class="h-4 w-4 shrink-0 text-gray-500" />
            <font-awesome-icon v-else-if="selectionChipIcon" :icon="selectionChipIcon" class="text-gray-500 text-sm shrink-0" />
            <span class="text-sm font-medium text-gray-700 flex-1" :class="selectionStateClass">
              <template v-if="selectionMessage">{{ selectionMessage }}</template>
              <template v-else>
                <span class="font-bold">{{ selectionCount }}</span>
                {{ ` ${t('shared.component.infuse.table.itemSelected')}` }}
              </template>
            </span>
            <font-awesome-icon
              :icon="['far', chipsExpanded ? 'chevron-up' : 'chevron-down']"
              class="text-gray-400 text-xs shrink-0"
            />
          </button>
          <div v-if="chipsExpanded" class="px-4 py-3 flex flex-wrap gap-2 border-t border-gray-100">
            <div
              v-for="row in allSelected"
              :key="getRowKey(row)"
              class="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-100 py-1 pl-3 pr-2"
            >
              <component :is="selectionChipIconComponent" v-if="selectionChipIconComponent" class="h-3.5 w-3.5 shrink-0 text-gray-500" />
              <font-awesome-icon v-else-if="selectionChipIcon" :icon="selectionChipIcon" class="text-gray-400 text-xs shrink-0" />
              <span class="text-sm font-medium text-gray-700">
                {{ selectionChipLabel ? selectionChipLabel(row) : getRowKey(row) }}
              </span>
              <button
                type="button"
                class="flex items-center justify-center text-gray-400 hover:text-gray-600"
                :aria-label="t('shared.component.infuse.table.removeSelected', { label: selectionChipLabel ? selectionChipLabel(row) : getRowKey(row) })"
                @click="deselectChip(row)"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" class="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
        <div v-if="showSelectAllMatching" class="flex justify-center py-1">
          <button
            type="button"
            class="text-sm font-medium text-primary-500 underline hover:text-primary-600"
            @click="emit('select-all-matching')"
          >
            <slot name="selectAllMatching" :count="totalMatching">
              {{ t('shared.component.infuse.table.selectAllMatching', { count: totalMatching }) }}
            </slot>
          </button>
        </div>
        <slot name="selectionActions" :selected-rows="allSelected" />
      </div>
    </template>
    <div
      v-else-if="hasSelection"
      :class="dataTableCounterClass"
      class="sticky bottom-3 z-10 mx-auto flex min-h-12 w-fit shrink-0 max-w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-6 py-2 shadow-md"
    >
      <!-- Count and "select all" wrap as one unit, so a narrow screen breaks before the actions, never between them. -->
      <div class="flex flex-wrap items-center justify-center gap-x-4">
        <span class="py-2 text-sm shrink-0" :class="selectionStateClass">
          {{ selectionMessage || `${selectionCount} ${t('shared.component.infuse.table.itemSelected')}` }}
        </span>
        <button
          v-if="showSelectAllMatching"
          type="button"
          class="text-sm font-medium text-primary-500 underline hover:text-primary-600 shrink-0"
          @click="emit('select-all-matching')"
        >
          <slot name="selectAllMatching" :count="totalMatching">
            {{ t('shared.component.infuse.table.selectAllMatching', { count: totalMatching }) }}
          </slot>
        </button>
        <!-- Also the only way out of a selection on mobile, which has no header checkbox. -->
        <button
          type="button"
          class="text-sm font-medium text-neutral-500 underline hover:text-neutral-700 shrink-0 md:hidden"
          @click="clearSelection()"
        >
          {{ t('shared.component.infuse.table.clearSelection') }}
        </button>
      </div>
      <slot name="selectionActions" :selected-rows="allSelected" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { vPermission } from '@infuse/iam/permission/directives';
import {
  ColumnSort,
  Filter,
  FilterConfiguration,
  FilterConfigurationType,
  Order,
  UiRowAction,
  UiTableColumn,
  UiTableColumnAlignment
} from '@infuse/ui/domain';
import { getFieldFromPath } from '@infuse/utils';
import { useInfiniteScroll } from '@vueuse/core';
import { debounce } from 'lodash';
import { computed, ref, unref, useSlots, watch } from 'vue';
import type { Component, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { InfuseCopy, InfuseFilterDropdown, InfuseIconButton, InfusePill } from '../index';
import InfuseButton from './InfuseButton.vue';
import InfuseCheckBox from './InfuseCheckBox.vue';
import InfuseInput from './InfuseInput.vue';
import InfuseSpinner from './InfuseSpinner.vue';
import InfuseTooltip from './InfuseTooltip.vue';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { hasRequiredPermissions } from '@infuse/ui/utils';

interface Props {
  columns: UiTableColumn[];
  actions?: UiRowAction[];
  rows: any[];
  active?: (row: any, id?: number) => boolean;
  downloadable?: boolean;
  downloadPermissionId?: string;
  downloadPermissionIds?: string[];
  editable?: boolean;
  hasMoreItems?: boolean;
  highlightRow?: number | null;
  loading?: boolean;
  addable?: boolean;
  selectable?: boolean;
  refreshable?: boolean;
  removable?: boolean;
  rowKey?: string | ((row: any) => string | number);
  search?: {
    query?: string;
    placeholder?: string;
  };
  verticalSize?: 'compact' | 'medium' | 'expanded';
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
  disabledRows?: any[];
  noDataTitle?: string;
  noDataDescription?: string;
  removePermissionId?: string;
  removePermissionIds?: string[];
  editPermissionId?: string;
  editPermissionIds?: string[];
  classVisitor?: (key: string) => string;
  selectCounterInTable?: boolean;
  clickPermissionId?: string;
  filterButtonSize?: 'small' | 'medium' | 'large';
  searchSize?: 'xs' | 'small' | 'medium' | 'large';
  preSelected?: any[];
  loadMoreColor?: 'primary' | 'secondary' | 'neutral';
  clickable?: boolean;
  /** Narrows `clickable` per row: rejected rows get no pointer and emit no `click-row`. */
  rowClickable?: (row: any) => boolean;
  /** Narrows `removable` per row: rejected rows get a disabled delete action. */
  rowRemovable?: (row: any) => boolean;
  /** Tooltip explaining why `rowRemovable` rejected a row. */
  rowRemovableTooltip?: (row: any) => string;
  showSelectionChips?: boolean;
  selectionChipLabel?: (row: any) => string;
  selectionChipIcon?: string[];
  selectionChipIconComponent?: Component;
  /** Total rows matching the current query/filter (may exceed the loaded `rows`).
   *  Enables the "select all N matching" affordance when `enableSelectAllMatching`. */
  totalMatching?: number;
  /** Opt-in: show a "select all {totalMatching}" note when more rows match than are
   *  loaded and clicking it emits `select-all-matching`. Off by default so existing
   *  consumers are unchanged. The consumer decides what selecting-all-matching means. */
  enableSelectAllMatching?: boolean;
  /** Colours the selection-count line to signal bulk-action thresholds. Driven by the
   *  consumer (it owns the limits); the table only styles what it is told. */
  selectionState?: 'normal' | 'warn' | 'block';
  /** True when "select all N matching" is active: the count line reads "N selected"
   *  (from `totalMatching`) and the select-all link is suppressed. */
  matchingSelected?: boolean;
  /** Rows the consumer took out of a "select all N matching" selection; shown unchecked and subtracted from the count. */
  excludedRowKeys?: (string | number)[];
  /** When set, replaces the "N selected" count line entirely (e.g. a block message
   *  that already states the count). Keeps the banner to a single line. */
  selectionMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  columns: () => [],
  actions: () => [],
  active: () => false,
  addable: false,
  downloadable: false,
  downloadPermissionId: undefined,
  downloadPermissionIds: undefined,
  editable: false,
  hasMoreItems: true,
  highlightRow: null,
  refreshable: false,
  removable: false,
  rowKey: 'id',
  selectable: false,
  totalMatching: undefined,
  enableSelectAllMatching: false,
  selectionState: 'normal',
  matchingSelected: false,
  excludedRowKeys: () => [],
  selectionMessage: undefined,
  search: undefined,
  verticalSize: 'expanded',
  filters: () => [],
  filterConfigs: () => [],
  disabledRows: () => [],
  reverseActions: false,
  noDataTitle: '',
  noDataDescription: '',
  removePermissionId: undefined,
  removePermissionIds: undefined,
  editPermissionId: undefined,
  editPermissionIds: undefined,
  classVisitor: undefined,
  selectCounterInTable: true,
  clickPermissionId: undefined,
  filterButtonSize: 'medium',
  searchSize: 'xs',
  preSelected: () => [],
  clickable: true,
  rowClickable: undefined,
  rowRemovable: undefined,
  rowRemovableTooltip: undefined
});

interface Emits {
  (e: 'add'): void;

  (e: 'add:filter', filter: Filter): void;

  (e: 'action', event: string, row: any): void;

  (e: 'click-row', row: any): void;

  (e: 'download', row: any): void;

  (e: 'edit', row: any): void;

  (e: 'load'): void;

  (e: 'refresh'): void;

  (e: 'remove', row: any): void;

  (e: 'remove:filter', filter: Filter): void;

  (e: 'search', query: string): void;

  (e: 'selected', rows: any[]): void;
  (e: 'select-all-matching'): void;
  /** "Select all N matching" is active and the user toggled one row: the consumer owns that selection. */
  (e: 'toggle-matching-row', row: any): void;
  /** "Select all N matching" is active and the header checkbox re-selects the visible excluded rows. */
  (e: 'select-matching-on-screen', visibleRows: any[]): void;

  (e: 'sort', columnSortState: ColumnSort): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();


const dataTable = ref<HTMLElement | null>(null);
const showFiltersDiv = ref<HTMLElement | null>(null);
const showFilters = ref<boolean>(false);

const removePermissionIds = computed(() => {
  if (props.removePermissionIds) {
    return props.removePermissionIds;
  }

  return props.removePermissionId ? [props.removePermissionId] : [];
});

const downloadPermissionIds = computed(() => {
  if (props.downloadPermissionIds) {
    return props.downloadPermissionIds;
  }

  return props.downloadPermissionId ? [props.downloadPermissionId] : [];
});

const editPermissionIds = computed(() => {
  if (props.editPermissionIds) {
    return props.editPermissionIds;
  }

  return props.editPermissionId ? [props.editPermissionId] : [];
});

const debounceSearch = debounce((query: string) => {
  if (query !== props.search?.query) {
    emit('search', query);
  }
}, 500);

const disabledKeySet = computed(() => new Set(props.disabledRows.map((row) => getRowKey(row))));

// Clone preSelected so selection mutations (push/filter) never touch the caller-owned prop array.
// selectedRows only holds the current-page subset; allSelected tracks every pre-selected row across pages.
const preSelectedKeys = new Set((props.preSelected ?? []).map((r: any) => getRowKey(r)));
const selectedRows: Ref<any[]> = ref(
  (props.rows ?? []).filter((r: any) => preSelectedKeys.has(getRowKey(r)) && !isRowDisabled(r))
);
const allSelected: Ref<any[]> = ref((props.preSelected ?? []).filter((r: any) => !isRowDisabled(r)));
const chipsExpanded = ref(false);

// "Select all N matching" affordance: opt-in, and only when more rows match the
// current query/filter than are currently selected.
const showSelectAllMatching = computed(
  () =>
    props.enableSelectAllMatching &&
    !props.matchingSelected &&
    props.totalMatching != null &&
    props.totalMatching > allSelected.value.length
);

// In "all N matching" mode the count reflects the whole matching set, not the loaded page.
const selectionCount = computed(() =>
  props.matchingSelected ? (props.totalMatching ?? 0) - props.excludedRowKeys.length : allSelected.value.length
);

// In "all matching" mode the rows live with the consumer, so `allSelected` is empty while the selection is not.
const hasSelection = computed(() => (props.matchingSelected ? selectionCount.value > 0 : allSelected.value.length > 0));

const excludedKeySet = computed(() => new Set(props.excludedRowKeys));

function isRowChecked(row: any): boolean {
  if (props.matchingSelected) return !excludedKeySet.value.has(getRowKey(row));
  return selectedRows.value.some((r) => getRowKey(r) === getRowKey(row));
}

// Header checkbox display state. In "all N matching" mode the selection is the filter,
// not a list of rows, so `selectedRows` stays empty while everything matching is
// selected — show it ticked unless a visible row was excluded (see the row checkboxes).
// `rows.length` guards both branches: an empty table must never read as "all selected"
// — neither via `0 === 0`, nor via a still-active filter-wide selection whose current
// query returns no rows.
// Disabled rows are never part of a selection, so "all" means all rows that can be selected.
const selectableRows = computed(() => props.rows.filter((row) => !isRowDisabled(row)));

const allRowsAppearSelected = computed(
  () =>
    selectableRows.value.length > 0 &&
    (props.matchingSelected
      ? selectableRows.value.every((row) => !excludedKeySet.value.has(getRowKey(row)))
      : selectedRows.value.length === selectableRows.value.length)
);

function isRowDisabled(row: unknown): boolean {
  return disabledKeySet.value.has(getRowKey(row));
}

const selectionStateClass = computed(
  () =>
    ({
      normal: '',
      warn: 'text-warning-600 font-semibold',
      block: 'text-error-600 font-semibold'
    })[props.selectionState]
);

function deselectChip(row: any) {
  const key = getRowKey(row);
  allSelected.value = allSelected.value.filter((r) => getRowKey(r) !== key);
  selectedRows.value = selectedRows.value.filter((r) => getRowKey(r) !== key);
  emit('selected', [...allSelected.value]);
}

watch(
  () => props.preSelected,
  (val) => {
    if (val) {
      allSelected.value = [...val];
      const allKeys = new Set(val.map((r: any) => getRowKey(r)));
      selectedRows.value = props.rows.filter((r: any) => allKeys.has(getRowKey(r)));
    }
  }
);

const normalizedColumns = computed(() => {
  const cols = unref(props.columns);
  return Array.isArray(cols) ? cols : [];
});

function getRowKey(row: any): string | number {
  return typeof props.rowKey === 'function' ? props.rowKey(row) : row?.[props.rowKey];
}

watch(
  () => props.rows,
  (newRows) => {
    // Cross-page mode: selectedRows = current-page subset of allSelected.
    // allSelected keeps off-screen rows intact.
    const allKeys = new Set(allSelected.value.map((r) => getRowKey(r)));
    selectedRows.value = newRows.filter((r) => allKeys.has(getRowKey(r)));
  }
);

// Selection actions must never receive a row that turned disabled while selected.
watch(
  () => props.disabledRows,
  () => {
    const before = allSelected.value.length;
    allSelected.value = allSelected.value.filter((row) => !isRowDisabled(row));
    selectedRows.value = selectedRows.value.filter((row) => !isRowDisabled(row));
    if (allSelected.value.length !== before) {
      emit('selected', [...allSelected.value]);
    }
  }
);

// In "all matching" mode the consumer owns the selection; stale row picks would resurface once it ends.
// No emit: an empty `selected` would read as "deselect all" and end the matching mode right away.
watch(
  () => props.matchingSelected,
  (isMatching) => {
    if (!isMatching) return;
    selectedRows.value = [];
    allSelected.value = [];
  }
);

// Without checkboxes a selection can't be seen or undone, and would resurface when selection is switched back on.
watch(
  () => props.selectable,
  (isSelectable) => {
    if (isSelectable || (allSelected.value.length === 0 && selectedRows.value.length === 0)) {
      return;
    }

    selectedRows.value = [];
    allSelected.value = [];
    emit('selected', []);
  }
);

const columnsWithSortState = computed(() =>
  normalizedColumns.value.map((c) => ({
    ...c,
    state: { columnName: c.name, order: Order.NONE }
  }))
);

useInfiniteScroll(dataTable, () => {
  if (!props.loading) {
    emit('load');
  }
});

function selectAll() {
  if (props.matchingSelected) {
    emit('select-matching-on-screen', [...selectableRows.value]);
    return;
  }

  selectedRows.value = [...selectableRows.value];
  const currentKeys = new Set(props.rows.map((r: any) => getRowKey(r)));
  const offScreen = allSelected.value.filter((r) => !currentKeys.has(getRowKey(r)));
  allSelected.value = [...offScreen, ...selectedRows.value];
  emit('selected', [...allSelected.value]);
}

// Unlike deselectAll this drops every page, and the empty emit ends a "select all matching" as well.
function clearSelection() {
  selectedRows.value = [];
  allSelected.value = [];
  emit('selected', []);
}

function deselectAll() {
  selectedRows.value = [];
  const currentKeys = new Set(props.rows.map((r: any) => getRowKey(r)));
  allSelected.value = allSelected.value.filter((r) => !currentKeys.has(getRowKey(r)));
  emit('selected', [...allSelected.value]);
}

function sort(columnSortState: ColumnSort): void {
  switch (columnSortState.order) {
    case Order.NONE:
      columnSortState.order = Order.DESC;
      break;
    case Order.DESC:
      columnSortState.order = Order.ASC;
      break;
    case Order.ASC:
      columnSortState.order = Order.NONE;
      break;
  }

  emit('sort', columnSortState);
}

function getSortIcon(order: Order): [string, string] {
  switch (order) {
    case Order.NONE:
      return ['fas', 'sort'];
    case Order.DESC:
      return ['fad', 'sort-down'];
    case Order.ASC:
      return ['fad', 'sort-up'];
  }
}

function selectRow(isSelected: boolean, row: any) {
  const key = getRowKey(row);

  if (props.matchingSelected) {
    emit('toggle-matching-row', row);
    return;
  }

  if (isSelected) {
    selectedRows.value.push(row);
  } else {
    selectedRows.value = selectedRows.value.filter((r) => getRowKey(r) !== key);
  }

  if (isSelected) {
    if (!allSelected.value.some((r) => getRowKey(r) === key)) {
      allSelected.value.push(row);
    }
  } else {
    allSelected.value = allSelected.value.filter((r) => getRowKey(r) !== key);
  }
  emit('selected', [...allSelected.value]);
}

function rowIsClickable(row: any): boolean {
  if (!props.clickable) return false;
  return props.rowClickable ? props.rowClickable(row) : true;
}

function rowIsRemovable(row: any): boolean {
  if (!props.removable) return false;
  return props.rowRemovable ? props.rowRemovable(row) : true;
}

function clickRow(row: any) {
  if (!rowIsClickable(row)) return;
  if (props.clickPermissionId && !hasRequiredPermissions([props.clickPermissionId], 'all', false)) {
    return;
  }
  emit('click-row', row);
}

const get = (obj: unknown, path: any, defValue?: unknown) => getFieldFromPath(obj, path, defValue);

const tableSlots = useSlots();

// A column shows on the mobile card when it is not explicitly hidden for
// mobile and has a custom cell template or an actual value — empty "Label:"
// rows are just noise on a phone.
function hasMobileValue(row: object, column: UiTableColumn): boolean {
  if (column.hideOnMobile) {
    return false;
  }

  if (tableSlots[column.name]) {
    return true;
  }

  const value = get(row, column.field);
  return value !== undefined && value !== null && value !== '';
}

const determineOperator = (filter: Filter): string => {
  if (filter.config.type === FilterConfigurationType.GT || filter.config.type === FilterConfigurationType.STRING_GT) {
    return filter.negated ? '≤' : '>';
  } else if (
    filter.config.type === FilterConfigurationType.GTE ||
    filter.config.type === FilterConfigurationType.STRING_GTE
  ) {
    return filter.negated ? '<' : '≥';
  } else if (
    filter.config.type === FilterConfigurationType.LT ||
    filter.config.type === FilterConfigurationType.STRING_LT
  ) {
    return filter.negated ? '≥' : '<';
  } else if (
    filter.config.type === FilterConfigurationType.LTE ||
    filter.config.type === FilterConfigurationType.STRING_LTE
  ) {
    return filter.negated ? '>' : '≤';
  }
  return filter.negated ? t('shared.component.infuse.table.filter.negated') : '';
};

const defaultTableClasses: Record<string, string> = {
  dataTableClass: 'overflow-x-auto',
  dataTableHeadClass: 'bg-neutral-50 text-neutral-500 top-0',
  dataTableHeadFilterClass: 'flex w-full items-center gap-4',
  dataTableBodyClass: "before:block before:leading-5 before:text-transparent before:content-['@']",
  dataTableBodyRowClass: 'text-neutral-500 transition-colors duration-150 hover:bg-neutral-50/[50%]',
  dataTableBodyRowActionsClass: 'text-neutral-500 transition-colors duration-150 hover:bg-neutral-50/[50%]',
  dataTableCounterClass: 'sticky bottom-0 z-10 w-full rounded-md border border-gray-200 bg-white px-4 py-2 shadow-sm'
};

const dataTableClass = ref(defaultTableClasses.dataTableClass);
const dataTableHeadClass = ref(defaultTableClasses.dataTableHeadClass);
const dataTableHeadFilterClass = ref(defaultTableClasses.dataTableHeadFilterClass);
const dataTableBodyClass = ref(defaultTableClasses.dataTableBodyClass);
const dataTableBodyRowClass = ref(defaultTableClasses.dataTableBodyRowClass);
const dataTableBodyRowActionsClass = ref(defaultTableClasses.dataTableBodyRowActionsClass);
const dataTableCounterClass = ref(defaultTableClasses.dataTableCounterClass);

watch(
  () => props.classVisitor,
  (visitorFn) => {
    if (typeof visitorFn === 'function') {
      // a falsy return keeps the default, so visitors only need to override the classes they care about
      dataTableClass.value = visitorFn('dataTableClass') || defaultTableClasses.dataTableClass;
      dataTableHeadClass.value = visitorFn('dataTableHeadClass') || defaultTableClasses.dataTableHeadClass;
      dataTableHeadFilterClass.value =
        visitorFn('dataTableHeadFilterClass') || defaultTableClasses.dataTableHeadFilterClass;
      dataTableBodyClass.value = visitorFn('dataTableBodyClass') || defaultTableClasses.dataTableBodyClass;
      dataTableBodyRowClass.value = visitorFn('dataTableBodyRowClass') || defaultTableClasses.dataTableBodyRowClass;
      dataTableBodyRowActionsClass.value =
        visitorFn('dataTableBodyRowActionsClass') || defaultTableClasses.dataTableBodyRowActionsClass;
      dataTableCounterClass.value = visitorFn('dataTableCounterClass') || defaultTableClasses.dataTableCounterClass;
    }
  },
  { immediate: true }
);
</script>
